// store/microsoft/microsoftSlice.ts
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { Client, MicrosoftState } from "./types";
import { fetchAllClients, completeSSOOnboarding } from "./microsoftThuck";

const initialState: MicrosoftState = {
  clientId: null,
  clients: [],
  loading: true,
  notGrantedConsentClients: [],
  grantedConsentClients: [],
  PIIClients: [],
  nonPIIClients: [],
  singleClientState: null,
  error: null,
};

export const MicrosoftSlice = createSlice({
  name: "microsoft",
  initialState,
  reducers: {
    setGrantedClients: (state, action: PayloadAction<Client[]>) => {
      state.grantedConsentClients = action.payload;
    },
    setNotGrantedClients: (state, action: PayloadAction<Client[]>) => {
      state.notGrantedConsentClients = action.payload;
    },
    setPIIProtectClients: (state, action: PayloadAction<Client[]>) => {
      state.PIIClients = action.payload;
    },
    setNonPIIProtectClients: (state, action: PayloadAction<Client[]>) => {
      state.nonPIIClients = action.payload;
    },
    setClients: (state, action: PayloadAction<Client[]>) => {
      // ensure azureTenants is always an array
      state.clients = action.payload.map((client) => ({
        ...client,
        azureTenants: client.azureTenants ?? [],
      }));
    },
    setSingleClientState: (state, action: PayloadAction<string>) => {
      state.singleClientState = action.payload;
    },
    setSingleClientId: (state, action: PayloadAction<string | null>) => {
      state.clientId = action.payload;
    },
    resetSSO: (state) => {
      state.notGrantedConsentClients = [];
      state.grantedConsentClients = [];
      state.PIIClients = [];
      state.nonPIIClients = [];
      state.singleClientState = null;
    },
    resetClients: (state) => {
      state.clients = [];
      state.singleClientState = null;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // fetchAllClients
      .addCase(fetchAllClients.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllClients.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(fetchAllClients.rejected, (state, action) => {
        state.loading = false;
        if (typeof action.payload === "string") {
          state.error = action.payload;
        } else {
          state.error = action.error.message ?? "Failed to fetch clients";
        }
      })

      // completeSSOOnboarding
      .addCase(completeSSOOnboarding.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(completeSSOOnboarding.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(completeSSOOnboarding.rejected, (state, action) => {
        state.loading = false;
        if (typeof action.payload === "string") {
          state.error = action.payload;
        } else {
          state.error = action.error.message ?? "SSO onboarding failed";
        }
      });
  },
});

export const {
  setGrantedClients,
  setNotGrantedClients,
  setPIIProtectClients,
  setNonPIIProtectClients,
  setClients,
  setSingleClientState,
  setSingleClientId,
  resetSSO,
  resetClients,
} = MicrosoftSlice.actions;

export default MicrosoftSlice.reducer;
